import { Organization, OrganizationServiceStatus } from '../types/organization.type';
import { PaymentMonitoringItem } from '../types/subscription.type';
import { INVOICE_COLORS } from '../pages/dashboard/invoiceTheme';

export const ORGANIZATION_SERVICE_STATUSES: OrganizationServiceStatus[] = [
  'free',
  'premium',
  'pending',
];

type ServiceStatusTheme = {
  color: string;
  badgeBg: string;
};

const SERVICE_STATUS_THEMES: Record<OrganizationServiceStatus, ServiceStatusTheme> = {
  premium: {
    color: INVOICE_COLORS.green,
    badgeBg: 'rgba(83, 194, 149, 0.12)',
  },
  pending: {
    color: '#F59E0B',
    badgeBg: 'rgba(245, 158, 11, 0.12)',
  },
  free: {
    color: INVOICE_COLORS.textMuted,
    badgeBg: 'rgba(154, 163, 178, 0.15)',
  },
};

export function resolveServiceStatus(
  org: Pick<Organization, 'service_status' | 'is_premium'>,
): OrganizationServiceStatus {
  if (org.service_status) return org.service_status;
  return org.is_premium ? 'premium' : 'free';
}

export function isPremiumOrg(org: Pick<Organization, 'service_status' | 'is_premium'>): boolean {
  return resolveServiceStatus(org) === 'premium';
}

export function isPendingOrg(org: Pick<Organization, 'service_status' | 'is_premium'>): boolean {
  return resolveServiceStatus(org) === 'pending';
}

export function isFreeOrg(org: Pick<Organization, 'service_status' | 'is_premium'>): boolean {
  return resolveServiceStatus(org) === 'free';
}

export function serviceStatusToIsPremium(status: OrganizationServiceStatus): boolean {
  return status === 'premium';
}

export function getServiceStatusTheme(status: OrganizationServiceStatus): ServiceStatusTheme {
  return SERVICE_STATUS_THEMES[status];
}

export function serviceStatusLabelKey(status: OrganizationServiceStatus): string {
  return `organizations.serviceStatus.${status}`;
}

export function normalizeOrganizationServiceStatus(org: Organization): Organization {
  const service_status = resolveServiceStatus(org);
  return {
    ...org,
    service_status,
    is_premium: service_status === 'premium',
  };
}

export function buildServiceStatusUpdateFields(status: OrganizationServiceStatus): {
  service_status: OrganizationServiceStatus;
  is_premium: boolean;
} {
  return {
    service_status: status,
    is_premium: serviceStatusToIsPremium(status),
  };
}

export function resolvePaymentMonitoringServiceStatus(
  item: Pick<PaymentMonitoringItem, 'organization_id' | 'service_status'>,
  orgStatusById?: ReadonlyMap<string, OrganizationServiceStatus>,
): OrganizationServiceStatus | undefined {
  if (item.service_status) return item.service_status;
  return orgStatusById?.get(item.organization_id);
}

export function getPaymentMonitoringDisplayStatusKey(
  item: PaymentMonitoringItem,
  orgStatusById?: ReadonlyMap<string, OrganizationServiceStatus>,
): string {
  if (resolvePaymentMonitoringServiceStatus(item, orgStatusById) === 'pending') {
    return 'pending';
  }
  return item.subscription_status;
}

export function isPaymentMonitoringItemPending(
  item: PaymentMonitoringItem,
  orgStatusById?: ReadonlyMap<string, OrganizationServiceStatus>,
): boolean {
  return resolvePaymentMonitoringServiceStatus(item, orgStatusById) === 'pending';
}
